import React, { useState, useEffect, useRef } from 'react';
import { fetchAppointmentsByDate } from '../../services/General/citaServiceGraph';
import { fetchParkings } from '../../services/Administracion/parkingsService';
import { AppointmentDetail, AppointmentStatus } from '../../types/appointment';
import { Parking } from '../../types/parking';
import { formatTimeFromISO, formatDateToText, getLocalDateString, formatDate, parseLocalDateTime, calculateDuration } from '../../utils/fechaUtil';
import { getStatusColor, getStatusText } from '../../utils/appointmentHelpers';

interface VisitStats {
  totalHoy: number;
  agendadas: number;
  enInstalaciones: number;
  finalizadas: number;
  atrasadas: number;
  noAsistidas: number;
}

const PanelSupervisorVisitas: React.FC = () => {
  const [visits, setVisits] = useState<AppointmentDetail[]>([]);
  const [parkings, setParkings] = useState<Parking[]>([]);
  const [stats, setStats] = useState<VisitStats>({
    totalHoy: 0,
    agendadas: 0,
    enInstalaciones: 0,
    finalizadas: 0,
    atrasadas: 0,
    noAsistidas: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState<'agendadas' | 'eninstalaciones' | 'finalizadas'>('agendadas');
  const [searchDate, setSearchDate] = useState<string>(() => {
    return getLocalDateString();
  });

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cargar parkings desde la API
  const loadParkings = async () => {
    try {
      const parkingsData = await fetchParkings();
      setParkings(parkingsData);
      console.log('Parkings cargados:', parkingsData.length);
    } catch (error: any) {
      console.error('Error cargando parkings:', error);
      // No mostramos error al usuario porque no es crítico para el funcionamiento
    }
  };

  // Función para obtener el nombre del parqueo por ID
  const getParkingName = (parkingId: number | null | undefined): string => {
    if (!parkingId) return '';

    const parking = parkings.find(p => p.parking_id === parkingId);
    if (!parking) return `Parqueo #${parkingId}`;

    // Retornar formato: "Nombre - Ubicación" o solo "Nombre" si no hay ubicación
    if (parking.location && parking.location.trim() !== '') {
      return `${parking.parking_name} - ${parking.location}`;
    }
    return parking.parking_name;
  };

  // Función para obtener solo el nombre del parqueo (sin ubicación)
  const getParkingShortName = (parkingId: number | null | undefined): string => {
    if (!parkingId) return '';

    const parking = parkings.find(p => p.parking_id === parkingId);
    return parking ? parking.parking_name : `P${parkingId}`;
  };

  // Función para cargar datos del dashboard
  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Cargar parkings si no están cargados
      if (parkings.length === 0) {
        await loadParkings();
      }

      // Obtener citas para la fecha seleccionada
      const appointments = await fetchAppointmentsByDate(searchDate);
      setVisits(appointments);

      // Calcular estadísticas
      const agendadas = appointments.filter(v =>
        (v.current_status === AppointmentStatus.UPCOMING ||
          v.current_status === AppointmentStatus.SCHEDULED ||
          v.current_status === AppointmentStatus.LATE) &&
        v.is_active
      ).length;

      const enInstalaciones = appointments.filter(v =>
        v.current_status === AppointmentStatus.IN_PROGRESS &&
        v.is_active
      ).length;

      const finalizadas = appointments.filter(v =>
        v.current_status === AppointmentStatus.COMPLETED
      ).length;

      const atrasadas = appointments.filter(v =>
        v.current_status === AppointmentStatus.LATE &&
        v.is_active
      ).length;

      const noAsistidas = appointments.filter(v =>
        v.current_status === AppointmentStatus.MISSED
      ).length;

      setStats({
        totalHoy: appointments.length,
        agendadas,
        enInstalaciones,
        finalizadas,
        atrasadas,
        noAsistidas
      });

      setLastUpdated(new Date());
      console.log('Dashboard actualizado:', new Date().toLocaleTimeString(), 'Parkings:', parkings.length);
    } catch (error: any) {
      console.error('Dashboard error:', error);
      setError('Error al cargar el panel de supervisión. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    loadDashboardData();
    console.log('Buscando la fecha:', searchDate);

    // Configurar actualización automática cada 5 minutos
    refreshIntervalRef.current = setInterval(() => {
      if (!loading) {
        loadDashboardData();
      }
    }, 5 * 60 * 1000); // 5 minutos

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [searchDate]);

  // Efecto para cargar parkings una sola vez al inicio
  useEffect(() => {
    if (parkings.length === 0) {
      loadParkings();
    }
  }, []);

  // Manejar cambio de fecha
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchDate(e.target.value);
  };

  // Filtrar visitas por estado
  const agendadasVisits = visits.filter(v =>
    (v.current_status === AppointmentStatus.UPCOMING ||
      v.current_status === AppointmentStatus.SCHEDULED ||
      v.current_status === AppointmentStatus.LATE) &&
    v.is_active
  );

  const enInstalacionesVisits = visits.filter(v =>
    v.current_status === AppointmentStatus.IN_PROGRESS &&
    v.is_active
  );

  const finalizadasVisits = visits.filter(v =>
    v.current_status === AppointmentStatus.COMPLETED ||
    v.current_status === AppointmentStatus.MISSED
  );

  // Función para obtener el badge de estado
  const getStatusBadge = (visit: AppointmentDetail) => {
    if (!visit.is_active) return <span className="badge bg-danger">Cancelada</span>;

    const statusColor = getStatusColor(visit.current_status);
    const statusText = getStatusText(visit.current_status);

    return (
      <span className={`badge bg-${statusColor}`}>
        {statusText}
        {visit.current_status === AppointmentStatus.LATE && visit.minutes_since_start && (
          <span className="ms-1">({visit.minutes_since_start} min)</span>
        )}
      </span>
    );
  };

  // Formatear hora
  const getTimeDisplay = (dateString: string | null) => {
    if (!dateString) return '-';
    return formatTimeFromISO(dateString);
  };

  // Calcular duración
  // const getDuration = (checkin: string | null, checkout: string | null) => {
  //   if (!checkin) return '-';

  //   // Usar la fecha actual
  //   const now = new Date();

  //   // Extraer la hora del checkin desde el string ISO o formato BD
  //   let checkinHour = 0, checkinMinute = 0;

  //   if (checkin.includes('T')) {
  //     // Formato ISO: "2026-03-03T13:03:00.000Z"
  //     const timePart = formatTimeFromISO(checkin); // Esto da "13:03"
  //     [checkinHour, checkinMinute] = timePart.split(':').map(Number);
  //   } else {
  //     // Formato BD: "2026-03-03 13:03:00"
  //     const timeMatch = checkin.match(/\s(\d{2}):(\d{2})/);
  //     if (timeMatch) {
  //       checkinHour = parseInt(timeMatch[1]);
  //       checkinMinute = parseInt(timeMatch[2]);
  //     }
  //   }

  //   // Crear fecha del checkin usando el día actual (para comparación)
  //   const checkinDate = new Date(now);
  //   checkinDate.setHours(checkinHour, checkinMinute, 0, 0);

  //   // Si la hora de checkin es mayor que la hora actual, asumimos que es del día anterior
  //   if (checkinDate > now) {
  //     checkinDate.setDate(checkinDate.getDate() - 1);
  //   }

  //   const durationMs = now.getTime() - checkinDate.getTime();
  //   const durationMins = Math.floor(durationMs / 60000);

  //   if (durationMins < 0) return '0 min';
  //   if (durationMins < 60) return `${durationMins} min`;

  //   const hours = Math.floor(durationMins / 60);
  //   const mins = durationMins % 60;
  //   return `${hours}h ${mins}m`;
  // };

  // Función para mostrar el tiempo restante o transcurrido
  const getTimeStatus = (visit: AppointmentDetail) => {
    const now = new Date();
    const startTime = parseLocalDateTime(visit.start_date);


    //Para visitas en instalaciones
    if (visit.current_status === AppointmentStatus.IN_PROGRESS && visit.checkin_time) {
      const checkinTime = parseLocalDateTime(visit.checkin_time);
      if (checkinTime) {
        const durationMs = now.getTime() - checkinTime.getTime();
        const durationMins = Math.floor(durationMs / 60000);
        return `⏱️ ${durationMins} min en instalaciones`;
      }
    }

    if (visit.current_status === AppointmentStatus.LATE) {
      return `⚠️ ${visit.minutes_since_start || 0} min de retraso`;
    }

    if (startTime && startTime > now) {
      const timeDiffMs = startTime?.getTime() - now.getTime();
      const timeDiffMins = Math.floor(timeDiffMs / 60000);

      if (timeDiffMins <= 30) {
        return `🕐 En ${timeDiffMins} min`;
      } else if (timeDiffMins <= 60) {
        return `🕐 En menos de 1 hora`;
      } else {
        const hours = Math.floor(timeDiffMins / 60);
        return `🕐 En ${hours} hora${hours > 1 ? 's' : ''}`;
      }
    }

    return '';
  };

  // Función para mostrar información del edificio y parqueo
  const getLocationInfo = (visit: AppointmentDetail) => {
    const parts = [];

    // Edificio asignado a la cita
    if (visit.building_name) {
      parts.push(`${visit.building_name}`);
    }

    // Edificio donde se hizo check-in (si es diferente)
    if (visit.checkin_building_name && visit.checkin_building_name !== visit.building_name) {
      parts.push(`Ingreso: ${visit.checkin_building_name}`);
    }

    // Parqueo asignado en check-in (si existe)
    if (visit.checkin_parking_id) {
      const parkingShortName = getParkingShortName(visit.checkin_parking_id);
      if (parkingShortName) {
        parts.push(`Parqueado en: ${parkingShortName}`);
      }
    }

    return parts.length > 0 ? parts.join(' • ') : 'No especificado';
  };

  // Renderizar tarjeta de visita para escritorio
  const renderDesktopCard = (visit: AppointmentDetail) => {
    const hasParkingInfo = visit.checkin_parking_id && parkings.length > 0;

    return (
      <div key={visit.id} className="border-bottom p-3 hover-light" title= {`Visita ID: ${visit.id} `}>
        <div className="d-flex justify-content-between align-items-start mb-2">

          <div >
            {/* <div className='m-2'>
              <span className={`badge bg-${getStatusColor(visit.current_status)}`}>
                <small className=''>{visit.id}</small>
              </span>
            </div> */}
            <h6 className="mb-0">{visit.guest_name}</h6>
            <small className="text-muted">{visit.guest_cedula || 'Cédula no registrada'}</small>
            
          </div>
          <div className="text-end">
            {getStatusBadge(visit)}
            <div className="small text-muted mt-1">
              {getTimeStatus(visit)}
            </div>
          </div>
        </div>

        <div className="small text-muted">
          <div className="row">
            <div className="col-4">
              <div><strong>Anfitrión:</strong></div>
              <div className="text-truncate" title={visit.host_email}>
                {visit.host_email}
              </div>
            </div>
            <div className="col-4">
              <div><strong>Hora cita:</strong></div>
              <div>{getTimeDisplay(visit.start_date)} - {getTimeDisplay(visit.end_date)}</div>
            </div>
            {visit.appointment_host_checkout_time && (
              <div className="col-4">
                <div><strong>Finalizada a las:</strong></div>
                <div>{getTimeDisplay(visit.appointment_host_checkout_time || '')}</div>
              </div>
            )}
          </div>
          <div className="row mt-2">
            <div className="col-4">
              <div><strong>Ubicación:</strong></div>
              <div className="text-truncate" title={getLocationInfo(visit)}>
                {getLocationInfo(visit)}
              </div>
            </div>
            <div className="col-6">
              <div><strong>Creada Por:</strong></div>
              <div className="text-truncate" title={visit.appointment_created_by}>
                {visit.appointment_created_by} - el : {formatDate(visit.appointment_created_date || '')} {formatTimeFromISO(visit.appointment_created_date || '')}
              </div>
            </div>
          </div>

          {visit.checkin_time && (
            <div className="row mt-2">
              <div className="col-6">
                <div><strong>Check-in:</strong></div>
                <div>
                  {getTimeDisplay(visit.checkin_time)}
                  {visit.checked_in_by && (
                    <div className="text-success">
                      <small><i className="fas fa-user-check me-1"></i>{visit.checked_in_by}</small>
                    </div>
                  )}
                </div>
              </div>
              {visit.checkout_time && (
                <div className="col-6">
                  <div><strong>Check-out:</strong></div>
                  <div>
                    {getTimeDisplay(visit.checkout_time)}
                    {visit.checked_out_by && (
                      <div className="text-secondary">
                        <small><i className="fas fa-user-times me-1"></i>{visit.checked_out_by}</small>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Información adicional para visitas en instalaciones */}
          {visit.current_status === AppointmentStatus.IN_PROGRESS && visit.checkin_time && (
            <div className="alert alert-warning mt-2 py-1 mb-0">
              <small>
                <i className="fas fa-clock me-1"></i>
                <strong>Tiempo interno:</strong> {calculateDuration(visit.checkin_time, null)}
                {hasParkingInfo && (
                  <>
                    <span className="mx-2">•</span>
                    <i className="fas fa-parking me-1"></i>
                    <strong>Parqueado en:</strong> {getParkingShortName(visit.checkin_parking_id)}
                  </>
                )}
              </small>
            </div>
          )}

          {/* Información adicional para visitas finalizadas */}
          {(visit.current_status === AppointmentStatus.COMPLETED || visit.current_status === AppointmentStatus.MISSED) && (
            <div className="alert alert-light mt-2 py-1 mb-0 border">
              <small>
                {visit.checkin_time && visit.checkout_time && (
                  <>
                    <i className="fas fa-history me-1"></i>
                    <strong>Duración total:</strong> {calculateDuration(visit.checkin_time, visit.checkout_time)}
                    {hasParkingInfo && (
                      <>
                        <span className="mx-2">•</span>
                        <i className="fas fa-parking me-1"></i>
                        <strong>Parqueado en:</strong> {getParkingShortName(visit.checkin_parking_id)}
                      </>
                    )}
                  </>
                )}
                {visit.current_status === AppointmentStatus.MISSED && (
                  <span className="text-danger">
                    <i className="fas fa-times-circle me-1"></i>
                    Visita no asistió
                  </span>
                )}
              </small>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Renderizar tarjeta de visita para móvil
  const renderMobileCard = (visit: AppointmentDetail) => {
    const hasParkingInfo = visit.checkin_parking_id && parkings.length > 0;

    return (
      <div key={visit.id} className="border-bottom p-3">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="flex-grow-1">
            <h6 className="mb-0">{visit.guest_name}</h6>
            <small className="text-muted">{visit.guest_cedula || 'Sin cédula'}</small>
          </div>
          {getStatusBadge(visit)}
        </div>

        <div className="small text-muted">
          <div><strong>Hora:</strong> {getTimeDisplay(visit.start_date)}</div>
          <div><strong>Anfitrión:</strong> {visit.host_email.split('@')[0]}</div>

          {visit.checkin_time && (
            <>
              <div><strong>Check-in:</strong> {getTimeDisplay(visit.checkin_time)}</div>
              {hasParkingInfo && (
                <div>
                  <strong>Parqueado en:</strong> {getParkingShortName(visit.checkin_parking_id)}
                </div>
              )}
              {visit.current_status === AppointmentStatus.IN_PROGRESS && (
                <div className="mt-1">
                  <span className="badge bg-warning text-dark">
                    <i className="fas fa-clock me-1"></i>
                    {calculateDuration(visit.checkin_time, null)}
                  </span>
                </div>
              )}
            </>
          )}

          {visit.checkout_time && (
            <div><strong>Check-out:</strong> {getTimeDisplay(visit.checkout_time)}</div>
          )}
        </div>
      </div>
    );
  };

  if (loading && visits.length === 0) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2 text-muted">Cargando panel de supervisión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      {/* Header del Dashboard */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
            <div>
              <h4 className="mb-1">Panel de Supervisión - Visitantes</h4>
              <p className="text-muted mb-0 small">
                <i className="fas fa-calendar-alt me-1"></i>
                {formatDateToText(searchDate)}
                {parkings.length > 0 && (
                  <span className="ms-2 text-success">
                    <i className="fas fa-parking me-1"></i>
                    {parkings.length} parqueos cargados
                  </span>
                )}
              </p>
            </div>

            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center mt-2 mt-md-0">
              <div className="me-sm-3 mb-2 mb-sm-0">
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={searchDate}
                  onChange={handleDateChange}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="text-end">
                <small className="text-muted d-block">
                  <i className="fas fa-sync-alt me-1"></i>
                  Actualizado: {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </small>
                <button
                  className="btn btn-outline-primary btn-sm mt-1"
                  onClick={loadDashboardData}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Actualizando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-redo-alt me-1"></i>
                      Actualizar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tarjetas de Estadísticas */}
        <div className="col-md-2 col-6 mb-3">
          <div className="card bg-primary text-white shadow-sm">
            <div className="card-body text-center p-2">
              <h4 className="card-title mb-0">{stats.totalHoy}</h4>
              <small className="opacity-75">Total Visitas</small>
            </div>
          </div>
        </div>

        <div className="col-md-2 col-6 mb-3">
          <div className="card bg-info text-white shadow-sm">
            <div className="card-body text-center p-2">
              <h4 className="card-title mb-0">{stats.agendadas}</h4>
              <small className="opacity-75">Agendadas</small>
            </div>
          </div>
        </div>

        <div className="col-md-2 col-6 mb-3">
          <div className="card bg-warning text-dark shadow-sm">
            <div className="card-body text-center p-2">
              <h4 className="card-title mb-0">{stats.enInstalaciones}</h4>
              <small className="opacity-75">En Instalaciones</small>
            </div>
          </div>
        </div>

        <div className="col-md-2 col-6 mb-3">
          <div className="card bg-success text-white shadow-sm">
            <div className="card-body text-center p-2">
              <h4 className="card-title mb-0">{stats.finalizadas}</h4>
              <small className="opacity-75">Finalizadas</small>
            </div>
          </div>
        </div>

        <div className="col-md-2 col-6 mb-3">
          <div className="card bg-danger text-white shadow-sm">
            <div className="card-body text-center p-2">
              <h4 className="card-title mb-0">{stats.atrasadas}</h4>
              <small className="opacity-75">Atrasadas</small>
            </div>
          </div>
        </div>

        <div className="col-md-2 col-6 mb-3">
          <div className="card bg-secondary text-white shadow-sm">
            <div className="card-body text-center p-2">
              <h4 className="card-title mb-0">{stats.noAsistidas}</h4>
              <small className="opacity-75">No Asistidas</small>
            </div>
          </div>
        </div>
      </div>

      {/* Mensajes de error */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Información del sistema */}
      <div className="alert alert-info mb-4 py-2">
        <div className="d-flex justify-content-between align-items-center">
          <small>
            <i className="fas fa-info-circle me-2"></i>
            El panel se actualiza automáticamente cada 5 minutos.
            Última actualización: {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            {parkings.length === 0 && (
              <span className="ms-2 text-warning">
                <i className="fas fa-exclamation-triangle me-1"></i>
                Cargando información de parqueos...
              </span>
            )}
          </small>
          <div className="small">
            <span className="badge bg-primary me-2">Total: {stats.totalHoy}</span>
            <span className="badge bg-info me-2">Agendadas: {stats.agendadas}</span>
            <span className="badge bg-warning text-dark me-2">En Inst: {stats.enInstalaciones}</span>
            <span className="badge bg-success">Finalizadas: {stats.finalizadas}</span>
          </div>
        </div>
      </div>

      {/* Vista para Escritorio (3 columnas) */}
      <div className="d-none d-md-block">
        <div className="row">
          {/* COLUMNA 1: AGENDADAS */}
          <div className="col-md-4 mb-4">
            <div className="card h-100 border-primary shadow-sm">
              <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-2">
                <h6 className="mb-0">
                  <i className="fas fa-calendar-alt me-2"></i>
                  Agendadas
                </h6>
                <span className="badge bg-light text-dark">{stats.agendadas}</span>
              </div>
              <div className="card-body p-0" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                {agendadasVisits.length === 0 ? (
                  <div className="text-center text-muted p-5">
                    <div className="fs-1 mb-3">📅</div>
                    <p className="mb-2">No hay visitas agendadas</p>
                    <small>Seleccione otra fecha o verifique las citas programadas</small>
                  </div>
                ) : (
                  agendadasVisits.map(renderDesktopCard)
                )}
              </div>
              <div className="card-footer bg-light py-1">
                <small className="text-muted">
                  <i className="fas fa-clock me-1"></i>
                  {stats.atrasadas > 0 && `${stats.atrasadas} atrasadas • `}
                  Actualizado: {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </small>
              </div>
            </div>
          </div>

          {/* COLUMNA 2: EN INSTALACIONES */}
          <div className="col-md-4 mb-4">
            <div className="card h-100 border-warning shadow-sm">
              <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center py-2">
                <h6 className="mb-0">
                  <i className="fas fa-users me-2"></i>
                  En Instalaciones
                </h6>
                <span className="badge bg-light text-dark">{stats.enInstalaciones}</span>
              </div>
              <div className="card-body p-0" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                {enInstalacionesVisits.length === 0 ? (
                  <div className="text-center text-muted p-5">
                    <div className="fs-1 mb-3">👥</div>
                    <p className="mb-2">No hay visitas en instalaciones</p>
                    <small>Todas las visitas han finalizado o aún no han llegado</small>
                  </div>
                ) : (
                  enInstalacionesVisits.map(renderDesktopCard)
                )}
              </div>
              <div className="card-footer bg-light py-1">
                <small className="text-muted">
                  <i className="fas fa-running me-1"></i>
                  {enInstalacionesVisits.length} visitas activas
                </small>
              </div>
            </div>
          </div>

          {/* COLUMNA 3: FINALIZADAS */}
          <div className="col-md-4 mb-4">
            <div className="card h-100 border-success shadow-sm">
              <div className="card-header bg-success text-white d-flex justify-content-between align-items-center py-2">
                <h6 className="mb-0">
                  <i className="fas fa-check-circle me-2"></i>
                  Finalizadas
                </h6>
                <span className="badge bg-light text-dark">{stats.finalizadas}</span>
              </div>
              <div className="card-body p-0" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                {finalizadasVisits.length === 0 ? (
                  <div className="text-center text-muted p-5">
                    <div className="fs-1 mb-3">✅</div>
                    <p className="mb-2">No hay visitas finalizadas</p>
                    <small>Todas las visitas están en curso o pendientes</small>
                  </div>
                ) : (
                  finalizadasVisits.map(renderDesktopCard)
                )}
              </div>
              <div className="card-footer bg-light py-1">
                <small className="text-muted">
                  <i className="fas fa-history me-1"></i>
                  {stats.noAsistidas > 0 && `${stats.noAsistidas} no asistidas • `}
                  Total: {stats.finalizadas}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vista para Móvil (Pestañas) */}
      <div className="d-block d-md-none">
        <div className="card shadow-sm">
          <div className="card-header p-0">
            <ul className="nav nav-tabs nav-justified" role="tablist">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'agendadas' ? 'active' : ''}`}
                  onClick={() => setActiveTab('agendadas')}
                >
                  <i className="fas fa-calendar-alt me-1"></i>
                  Agendadas
                  <span className="badge bg-primary ms-1">{stats.agendadas}</span>
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'eninstalaciones' ? 'active' : ''}`}
                  onClick={() => setActiveTab('eninstalaciones')}
                >
                  <i className="fas fa-users me-1"></i>
                  En Inst.
                  <span className="badge bg-warning text-dark ms-1">{stats.enInstalaciones}</span>
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'finalizadas' ? 'active' : ''}`}
                  onClick={() => setActiveTab('finalizadas')}
                >
                  <i className="fas fa-check-circle me-1"></i>
                  Final.
                  <span className="badge bg-success ms-1">{stats.finalizadas}</span>
                </button>
              </li>
            </ul>
          </div>

          <div className="card-body p-0">
            {/* Pestaña Agendadas - Móvil */}
            {activeTab === 'agendadas' && (
              <div style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                {agendadasVisits.length === 0 ? (
                  <div className="text-center text-muted p-5">
                    <div className="fs-1 mb-3">📅</div>
                    <p className="mb-0">No hay visitas agendadas</p>
                  </div>
                ) : (
                  agendadasVisits.map(renderMobileCard)
                )}
              </div>
            )}

            {/* Pestaña En Instalaciones - Móvil */}
            {activeTab === 'eninstalaciones' && (
              <div style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                {enInstalacionesVisits.length === 0 ? (
                  <div className="text-center text-muted p-5">
                    <div className="fs-1 mb-3">👥</div>
                    <p className="mb-0">No hay visitas en instalaciones</p>
                  </div>
                ) : (
                  enInstalacionesVisits.map(renderMobileCard)
                )}
              </div>
            )}

            {/* Pestaña Finalizadas - Móvil */}
            {activeTab === 'finalizadas' && (
              <div style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                {finalizadasVisits.length === 0 ? (
                  <div className="text-center text-muted p-5">
                    <div className="fs-1 mb-3">✅</div>
                    <p className="mb-0">No hay visitas finalizadas</p>
                  </div>
                ) : (
                  finalizadasVisits.map(renderMobileCard)
                )}
              </div>
            )}
          </div>

          <div className="card-footer py-1">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              {activeTab === 'agendadas' && `${stats.agendadas} visitas agendadas`}
              {activeTab === 'eninstalaciones' && `${stats.enInstalaciones} visitas en instalaciones`}
              {activeTab === 'finalizadas' && `${stats.finalizadas} visitas finalizadas`}
            </small>
          </div>
        </div>
      </div>

      {/* Pie de página */}
      <div className="row mt-2 mb-3">
        <div className="col-12">
          <div className="text-center">
            <small className="text-muted">
              <i className="fas fa-shield-alt me-1"></i>
              Sistema de Control de Visitantes •
              <span className="mx-2">•</span>
              {new Date().toLocaleDateString('es-CR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              <span className="mx-2">•</span>
              <i className="fas fa-clock me-1"></i>
              Actualización automática cada 5 minutos
              {parkings.length > 0 && (
                <>
                  <span className="mx-2">•</span>
                  <i className="fas fa-parking me-1"></i>
                  {parkings.length} parqueos
                </>
              )}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanelSupervisorVisitas;